import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import questsData from "./Guides/quests";

const Quests = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeQuest, setActiveQuest] = useState(null);
  const [showExpired, setShowExpired] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  // Separate quests
  const activeQuests = questsData.sections.filter((q) => q.status === "available");
  const expiredQuests = questsData.sections.filter((q) => q.status === "expired");

  // Filter by search term
  const filteredActive = activeQuests.filter(
    (q) => q.name.toLowerCase().includes(searchTerm.toLowerCase()) || q.npc.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const filteredExpired = expiredQuests.filter(
    (q) => q.name.toLowerCase().includes(searchTerm.toLowerCase()) || q.npc.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    if (id) {
      const quest = questsData.sections.find((q) => q.id.toString() === id.toString());
      if (quest) {
        setActiveQuest(quest);
      }
    }
  }, [id]);

  return (
    <div className="panel-style ">
      <h1 className="panel-heading">Quests</h1>
    <div className="md:h-[78vh] flex flex-col md:flex-row text-[var(--text-color)]">
      {/* Mobile sidebar toggle */}
      <button 
        onClick={() => setMobileSidebarOpen(!mobileSidebarOpen)} 
        className="md:hidden flex items-center justify-between p-4 bg-[var(--entity-bg-color)]"
      >
        <span className="flex items-center gap-2">
          <i className="fas fa-scroll"></i> {activeQuest?.name || "Select a Quest"}
        </span>
        <i className={`fas ${mobileSidebarOpen ? "fa-chevron-down" : "fa-chevron-right"}`}></i>
      </button>
  
      {/* Scrollable Sidebar */}
      <div className={`${mobileSidebarOpen ? "block" : "hidden"} md:block overflow-auto w-full md:w-80 bg-[var(--entity-bg-color)] p-4 border-r-2 border-[var(--outline-color)] flex flex-col`}>
        <div className="relative mb-4">
          <i className="fas fa-search absolute text-[var(--icons)] left-3 top-2"></i>
          <input
            type="text"
            placeholder="Search quests..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 select-forms focus:outline-none focus:ring-2"
          />
        </div>
  
        {/* Scrollable quest list container */}
        <div className="flex-1 overflow-y-auto">
          <h2 className="flex items-center gap-2 text-retroblue text-xl font-bold mb-2 sticky top-0 bg-[var(--entity-bg-color)] z-10 py-2">
            <i className="fas fa-scroll"></i> Active Quests ({filteredActive.length})
          </h2>
          <ul className="mb-6 space-y-1">
            {filteredActive.map((quest) => (
              <li key={quest.id}>
                <button
                  onClick={() => {
                    setActiveQuest(quest);
                    setMobileSidebarOpen(false); 
                    navigate(`/wiki/quests/${quest.id}`);
                  }}
                  className={`quest ${
                    activeQuest?.id === quest.id ? "activeQuest" : "inactiveQuest"
                  }`}
                >
                  {quest.name}
                </button>
              </li>
            ))}
          </ul>
  
          <button 
            onClick={() => setShowExpired(!showExpired)} 
            className="flex items-center gap-2 w-full p-2 rounded-lg mb-2 sticky top-0 z-10"
          >
            <i className="fas fa-history"></i>
            <span className="font-bold">Expired Quests ({expiredQuests.length})</span>
            <i className={`fas ${showExpired ? "fa-chevron-down" : "fa-chevron-right"} ml-auto`}></i>
          </button>
  
          {showExpired && (
            <ul className="space-y-1 opacity-75 pb-4">
              {filteredExpired.map((quest) => (
                <li key={quest.id}>
                  <button
                    onClick={() => {
                      setActiveQuest(quest);
                      setMobileSidebarOpen(false);
                      navigate(`/wiki/quests/${quest.id}`);
                    }}
                    className={`quest ${
                      activeQuest?.id === quest.id ? "activeQuest" : "inactiveQuest"
                    }`}
                  >
                    {quest.name}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
  
      {/* Main content with new layout */}
      <div className="flex-1 p-2 md:p-1 overflow-auto">
        {activeQuest ? (
          <div className="bg-[var(--entity-bg-color)] rounded-xl border-2 border-[var(--panel-outline-color)] p-3 shadow">
            {/* Status badge */}
            <div className={`inline-block px-3 py-1 rounded-full mb-4 text-sm ${
              activeQuest.status === "available" ? "bg-green-500 text-black font-semibold" : "bg-[var(--red)] text-white font-semibold"
            }`}>
              {activeQuest.status === "available" ? "Active" : "Expired"}
            </div>
  
            <h1 className="text-xl font-bold text-[var(--cute-headings)] mb-2">{activeQuest.name}</h1>
            <h2 className="text-sm text-[var(--text3-color)] mb-3">
              <i className="fas fa-user-tag"></i> <b>Given by:</b> {activeQuest.npc} at {activeQuest.location}
            </h2>
            {activeQuest.prerequisites && (<h2 className="text-sm text-[var(--text3-color)] mb-6"><i className="fa-solid fa-backward"></i> <b>Prerequisites:</b> {activeQuest.prerequisites}</h2>)}
  
            {/* Other Info with HTML support */}
            {activeQuest.otherinfo && (
              <div 
              className="text-sm bg-[var(--entity-mini)] bg-opacity-30 p-3 rounded-lg mb-3 border-l-4 border-[var(--panel-outline-color)]">
              <span className="text-[var(--text3-color)]"><i className="fas fa-clock"></i> <b>Event Period: </b></span>        
               <p className="text-xs" dangerouslySetInnerHTML={{ __html: activeQuest.otherinfo }}
              /></div>
            )}
  
            {/* Main content area - Description takes full width */}
            <div className="mb-6">
              <div className="bg-[var(--entity-mini)] bg-opacity-50 p-4 rounded-lg border border-[var(--panel-outline-color)] text-sm">
                <h3 className="text-lg font-bold text-[var(--text3-color)] mb-3">
                  <i className="fas fa-book-open"></i> Description
                </h3>
                <div 
                  className="prose prose-invert max-w-none" 
                  dangerouslySetInnerHTML={{ __html: activeQuest.description }} 
                />
              </div>
            </div>
  
            {/* Requirements and Rewards side by side below */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="text-xs bg-[var(--entity-mini)] bg-opacity-50 p-4 rounded-lg border border-[var(--panel-outline-color)]">
              <h3 className="text-lg font-bold text-[var(--text3-color)] mb-3">
                  <i className="fas fa-backpack"></i> Required Items
                </h3>
                <ul className="list-disc pl-5">
                  {activeQuest.items.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </div>
  
              <div className="text-xs bg-[var(--entity-mini)] bg-opacity-50 p-4 rounded-lg border border-[var(--panel-outline-color)]">
              <h3 className="text-lg font-bold text-[var(--text3-color)] mb-3">
                  <i className="fas fa-trophy"></i> Reward
                </h3>
                <div dangerouslySetInnerHTML={{ __html: activeQuest.reward || "None" }} />
              </div>
            </div>
  
            {activeQuest.image?.length > 0 && (
              <div className="mt-6 text-xs">
                <h3 className="text-lg font-bold text-[var(--text3-color)] mb-3">
                  <i className="fas fa-images"></i> Images
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {activeQuest.image.map((img, i) => (
                    <img 
                      key={i} 
                      src={img} 
                      alt={`Quest reference ${i + 1}`} 
                      className="rounded-lg border-2 border-retropink max-h-64 object-contain mx-auto" 
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-[var(--entity-bg-color)] rounded-xl border-2 border-[var(--panel-outline-color)] p-3 shadow flex flex-col items-center justify-center h-full text-center">
            <div className="text-5xl mb-4">(◕‿◕✿)</div>
            <h2 className="text-2xl font-bold text-[var(--text-color)] mb-2">
              <i className="fas fa-map-signs"></i> Welcome to the Quest Guide!
            </h2>
            <p className="text-[var(--text-color)] max-w-md">
              Select a quest from the sidebar to view all the details.
              <br />
              Active quests are shown first, but you can view expired ones too!
            </p>
          </div>
        )}
      </div>
    </div></div>
  );
};

export default Quests;
